import {
  ButtonV2,
  LabraToolTipV2,
  PlanType,
  VerticalDivider,
  StatusBarV4,
  StatusBarStatusColorsV4,
  StatusBarColorsV4,
} from '@labrav/react-components'
import { OfferStatus, PrivateOffer } from '../../../modules/privateOffer/types'
import {
  OfferHeaderActions,
  type PrivateOfferViewTrackingHandlers,
} from './OfferHeaderActions/OfferHeaderActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faClock,
  faHourglassStart,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import { productDetailDate } from '../../utils/productDetailDate'
import { useStyles } from './OfferHeader.styles'
import { getStatusAndLabelForPrivateOffer } from '../../utils/getStatusAndLabelForPrivateOffer'
import {
  getExpirationFromDate,
  getFormattedLabraDate,
  useAccountId,
  updateAppAlert,
  LoadingTypes,
  isLoading as Loading,
} from '@labrav/common'
import { Divider } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { updatePrivateOfferForUser } from '../../../modules/privateOffer/action'
import { cmcsExternalPageUrl, CRMTypes } from '../../../utils/constants'
import { useDispatch, useSelector } from '../../../store'
import {
  fetchSubscriptionList,
  getContractsData,
} from '../../../api/marketplace'
import {
  ExtendPrivateOfferExpiration,
  type UpdatePrivateOfferExpirationProps,
} from '../ExtendPrivateOfferExpiration'

type OfferHeaderProps = {
  privateOffer: PrivateOffer
  planType?: PlanType
  crmOpportunityId?: string
  externalBaseURL?: string
  search?: string
  crmType?: CRMTypes
  partnerId?: string
  viewTracking?: PrivateOfferViewTrackingHandlers
}

export const AWSOfferHeader = ({
  privateOffer,
  planType,
  crmOpportunityId,
  externalBaseURL,
  search,
  crmType,
  partnerId,
  viewTracking,
}: OfferHeaderProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { cloud: routeCloud, accountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''

  const routeAccountId = partnerId || accountId || fallbackAccountId || ''
  const { privateOfferId, offerStatus, error, updatedAt, offerExpirationDate } =
    privateOffer
  const formatedOfferExpirationDate = privateOffer.offerExpirationDate
    ? getFormattedLabraDate(privateOffer.offerExpirationDate).formattedDate
    : '-'
  const formattedAcceptedDate = privateOffer.auditJson?.offerAcceptedAt
    ? getFormattedLabraDate(privateOffer.auditJson?.offerAcceptedAt)
        .formattedDate
    : '-'
  const formattedAcceptedTime = privateOffer.auditJson?.offerAcceptedAt
    ? getFormattedLabraDate(privateOffer.auditJson?.offerAcceptedAt)
        .formattedTime
    : '-'
  const [copiedBuyerForm, setCopiedBuyerForm] = useState(false)
  const { fullDateTime, modifiedDate: modifiedUpdatedAtDate = '' } = updatedAt
    ? productDetailDate(updatedAt)
    : { fullDateTime: '', modifiedDate: '' }

  const {
    modifiedDate: offerExpirationDateDisplay,
    modifiedTime: offerExpirationDateTime,
  } = offerExpirationDate
    ? productDetailDate(offerExpirationDate)
    : { modifiedDate: '', modifiedTime: '' }

  const fullDateExpirationTimeForHeader = `${offerExpirationDateDisplay} ${offerExpirationDateTime}`

  const { isExpriringSoon, dayDiff } = getExpirationFromDate(
    offerExpirationDate,
    5
  )
  const offerExpiringSoon =
    isExpriringSoon &&
    ![
      OfferStatus.PRIVATE_OFFER_ACCEPTED,
      OfferStatus.PRIVATE_OFFER_EXPIRED,
      OfferStatus.PRIVATE_OFFER_RESTRCITED,
      OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_FAILED,
    ].includes(offerStatus)

  const statusLabel = getStatusAndLabelForPrivateOffer(offerStatus)

  const [openEditOfferModal, setOpenEditOfferModal] = useState(false)

  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  const handleClickEditFailedPO = () => {
    setOpenEditOfferModal(true)
  }
  const handleEditOfferCloseModal = () => {
    setOpenEditOfferModal(false)
  }
  const handleEditOfferConfirm = async () => {
    await dispatch(
      updatePrivateOfferForUser(
        privateOfferId,
        {},
        undefined,
        true,
        handleEditOfferCloseModal,
        undefined,
        routeAccountId
      )
    )

    if (!privateOffer.awsProductId || !privateOfferId) {
      console.warn(
        'Cannot navigate to edit offer: missing awsProductId or privateOfferId'
      )
      return
    }

    const editOfferURL =
      routeCloud && routeAccountId
        ? `/flyout/${routeCloud}/${routeAccountId}/products-listing/${privateOffer.awsProductId}/private-offers/${privateOfferId}/edit`
        : `/flyout/products-listing/${privateOffer.awsProductId}/private-offers/${privateOfferId}/edit`

    navigate(editOfferURL)
  }
  const shouldShowFillBuyerForm =
    !privateOffer?.buyerSubscriptionData?.formFilled &&
    planType !== PlanType.AMI

  const pickBuyerId = (buyerId?: string | null) => {
    if (
      !buyerId ||
      buyerId === 'null' ||
      buyerId === 'undefined' ||
      buyerId === 'None'
    ) {
      return ''
    }
    return String(buyerId)
  }

  const extractBuyerIdFromSubscriptions = (
    subscriptions: Array<{
      buyer_id?: string
      buyerId?: string
      subscription_id?: string
      subscriptionId?: string
      private_offer_id?: string
      privateOfferId?: string
      product_id?: string
      productId?: string
    }>
  ) => {
    if (!subscriptions.length) {
      return { buyerId: '', productId: '' }
    }
    const matched =
      subscriptions.find(subscription => {
        const subscriptionId =
          subscription.subscription_id || subscription.subscriptionId
        const offerId =
          subscription.private_offer_id || subscription.privateOfferId
        return (
          (!!privateOffer.labraSubscriptionId &&
            subscriptionId === privateOffer.labraSubscriptionId) ||
          (!!privateOffer.privateOfferId &&
            offerId === privateOffer.privateOfferId)
        )
      }) || subscriptions[0]

    return {
      buyerId: pickBuyerId(matched?.buyer_id || matched?.buyerId),
      productId: matched?.product_id || matched?.productId || '',
    }
  }

  const fetchSubscriptions = async (filters: {
    privateOfferId?: string
    productId?: string
    awsAccountId?: string
    awsOfferId?: string
    awsAgreementId?: string
  }) => {
    const response = await fetchSubscriptionList({
      partnerId: routeAccountId,
      pageSize: 50,
      pageNumber: 1,
      status: 'All',
      ...filters,
    })
    const data = response?.data as
      | {
          subscriptions?: Array<{
            buyer_id?: string
            buyerId?: string
            subscription_id?: string
            subscriptionId?: string
            private_offer_id?: string
            privateOfferId?: string
            product_id?: string
            productId?: string
          }>
          rows?: Array<{
            buyer_id?: string
            buyerId?: string
            subscription_id?: string
            subscriptionId?: string
            private_offer_id?: string
            privateOfferId?: string
            product_id?: string
            productId?: string
          }>
        }
      | undefined
    return data?.subscriptions || data?.rows || []
  }

  const getBuyerRegistrationBuyerId = async () => {
    const fromOffer = pickBuyerId(privateOffer?.buyerSubscriptionData?.buyerId)
    const fallbackProductId =
      privateOffer.awsProductId || privateOffer.labraProductId || ''
    if (fromOffer) {
      return {
        buyerId: fromOffer,
        productId: fallbackProductId,
      }
    }

    if (!routeAccountId) {
      return { buyerId: '', productId: '' }
    }

    try {
      const filterAttempts = [
        privateOffer.privateOfferId
          ? { privateOfferId: privateOffer.privateOfferId }
          : null,
        privateOffer.awsOfferId
          ? { awsOfferId: privateOffer.awsOfferId }
          : null,
        privateOffer.awsAccountId
          ? { awsAccountId: privateOffer.awsAccountId }
          : null,
        privateOffer.awsAgreementId
          ? { awsAgreementId: privateOffer.awsAgreementId }
          : null,
        fallbackProductId ? { productId: fallbackProductId } : null,
      ].filter(Boolean) as Array<{
        privateOfferId?: string
        productId?: string
        awsAccountId?: string
        awsOfferId?: string
        awsAgreementId?: string
      }>

      for (const filters of filterAttempts) {
        const subscriptions = await fetchSubscriptions(filters)
        const resolved = extractBuyerIdFromSubscriptions(subscriptions)
        if (resolved.buyerId) {
          return {
            buyerId: resolved.buyerId,
            productId: resolved.productId || fallbackProductId,
          }
        }
      }

      if (fallbackProductId) {
        const contractsResponse = await getContractsData(
          routeAccountId,
          fallbackProductId,
          privateOffer.labraSubscriptionId || undefined
        )
        const contractsData = contractsResponse?.data as
          | {
              contracts?: {
                rows?: Array<{
                  id?: string
                  buyer_id?: string
                  buyerId?: string
                }>
              }
              rows?: Array<{ id?: string; buyer_id?: string; buyerId?: string }>
            }
          | undefined
        const rows = contractsData?.contracts?.rows || contractsData?.rows || []
        const matched =
          rows.find(
            (contract: { id?: string; buyer_id?: string; buyerId?: string }) =>
              !!privateOffer.labraSubscriptionId &&
              contract.id === privateOffer.labraSubscriptionId
          ) || rows[0]
        const contractBuyerId = pickBuyerId(
          matched?.buyer_id || matched?.buyerId
        )
        if (contractBuyerId) {
          return { buyerId: contractBuyerId, productId: fallbackProductId }
        }
      }

      return { buyerId: '', productId: fallbackProductId }
    } catch (error) {
      console.error('Failed to resolve buyer id for registration form:', error)
      return { buyerId: '', productId: fallbackProductId }
    }
  }

  const getBuyerRegistrationFormUrl = ({
    buyerId,
    productId,
  }: {
    buyerId: string
    productId: string
  }) =>
    `${cmcsExternalPageUrl}/buyer_form?buyer_id=${buyerId}&product_name=${
      privateOffer?.productName || 'None'
    }&marketplace_url=None&buyer_exists=False&labra_product_id=${productId}`

  const redirectToBuyerRegistration = async () => {
    const { buyerId, productId } = await getBuyerRegistrationBuyerId()
    if (!buyerId) {
      if (privateOffer.labraSubscriptionId) {
        const cloudPath = routeCloud?.toLowerCase()
        const subscriptionDetailPath =
          routeCloud && routeAccountId
            ? `/flyout/${cloudPath}/${routeAccountId}/subscriptions/${privateOffer.labraSubscriptionId}/detail`
            : `/flyout/subscriptions/${privateOffer.labraSubscriptionId}/detail`
        navigate(subscriptionDetailPath)
        return
      }
      dispatch(
        updateAppAlert({
          message:
            'Buyer registration is unavailable because no buyer id was found for this accepted offer.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    window.open(getBuyerRegistrationFormUrl({ buyerId, productId }), '_blank')
  }

  const copyBuyerRegistrationForm = async () => {
    const { buyerId, productId } = await getBuyerRegistrationBuyerId()
    if (!buyerId) {
      dispatch(
        updateAppAlert({
          message:
            'Buyer registration link is unavailable because no buyer id was found for this accepted offer.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    navigator.clipboard
      .writeText(getBuyerRegistrationFormUrl({ buyerId, productId }))
      .then(() => {
        setCopiedBuyerForm(true)
        setTimeout(() => {
          setCopiedBuyerForm(false)
        }, 2000)
      })
      .catch(error => {
        console.error('Error copying URL to clipboard:', error)
      })
  }

  const handleViewSubscription = () => {
    const subscriptionId = privateOffer?.labraSubscriptionId
    const cloudPath = routeCloud?.toLowerCase()

    if (!subscriptionId) {
      console.warn(
        'Cannot navigate to subscription: labraSubscriptionId is missing'
      )
      return
    }

    const subscriptionDetailPath =
      routeCloud && routeAccountId
        ? `/flyout/${cloudPath}/${routeAccountId}/subscriptions/${subscriptionId}/detail`
        : `/flyout/subscriptions/${subscriptionId}/detail`

    navigate(subscriptionDetailPath)
  }

  const extendExpirationProps: UpdatePrivateOfferExpirationProps = {
    offerExpirationDate: formatedOfferExpirationDate,
    offerId: privateOffer?.privateOfferId,
    planType: planType ?? PlanType.SAAS_CONTRACT,
    offerSubscriptionEndDate: privateOffer?.subscriptionEndDate || '',
    offerStatus: offerStatus,
    isDraft: privateOffer?.draft,
    aggrementStartDate: privateOffer?.agreementStartDate,
    type: privateOffer?.type,
    labraSubscriptionId: privateOffer?.labraSubscriptionId,
    subscription: privateOffer?.subscription,
    partnerId: routeAccountId,
    cloudMarketplace: 'AWS',
  }

  return (
    <>
      {offerStatus === OfferStatus.PRIVATE_OFFER_FAILED && (
        <StatusBarV4
          status={statusLabel.label}
          backgroundColor={StatusBarColorsV4.RED}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          content={
            <div
              className={classes.errorSubContent}
              data-testid={'failed-offer-content'}
            >
              <div className={classes.errorMsg}>Issues found: {error}</div>
              <div className={classes.offerUpdatedAt}>{fullDateTime}</div>
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED && (
        <StatusBarV4
          status={statusLabel.label}
          backgroundColor={StatusBarColorsV4.RED}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          content={
            <div
              className={classes.expired}
              data-testid={'expired-offer-content'}
            >
              <div>Offer expired on {offerExpirationDateDisplay}.</div>
              {planType !== PlanType.AMI && (
                <>
                  <VerticalDivider height="24px" />
                  {
                    <div data-testid="extend-expiration-date-2">
                      {' '}
                      <ExtendPrivateOfferExpiration
                        {...extendExpirationProps}
                      />
                    </div>
                  }
                </>
              )}
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS && (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.RED}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          content={
            <div
              className={classes.cancellationInprogress}
              data-testid={'restriction-inprogress-offer-content'}
            >
              <FontAwesomeIcon
                icon={faHourglassStart}
                style={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  padding: '0px 12px 0px 0px',
                  color: '#2E3132',
                }}
              />
              <div>
                The offer cancellation is in progress and will be done in
                approximately 5 minutes.
              </div>
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED && (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.RED}
          divider
          statusColor={StatusBarStatusColorsV4.RED}
          status={statusLabel.label}
          content={`Offer has been cancelled on ${modifiedUpdatedAtDate}.`}
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED && (
        <div className={classes.offerAcceptedWrapper}>
          <StatusBarV4
            status={statusLabel.label}
            backgroundColor={StatusBarColorsV4.GREEN}
            divider
            statusColor={StatusBarStatusColorsV4.GREEN}
            content={
              <>
                <div
                  className={classes.offerAccepted}
                  data-testid={'accepted-offer-content'}
                >
                  <div>
                    Offer was accepted on {formattedAcceptedDate} |{' '}
                    {formattedAcceptedTime}
                  </div>
                  {planType !== PlanType.AMI && (
                    <ButtonV2
                      styleType="outlined"
                      className={classes.viewSubscription}
                      onClick={handleViewSubscription}
                    >
                      View contract
                    </ButtonV2>
                  )}
                </div>
              </>
            }
          />
          {shouldShowFillBuyerForm && (
            <div
              className={classes.offerAcceptedBody}
              data-testid={'accepted-offer-sub-content'}
            >
              <Divider />
              <div className={classes.acceptedSubheading}>
                <div className={classes.acceptedSubTitle}>
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    style={{
                      width: '16px',
                      height: '16px',
                      fontSize: '16px',
                      padding: '0px 12px 0px 0px',
                      color: '#2E3132',
                    }}
                  />
                  Buyer hasn't filled the registration form.
                </div>
                <VerticalDivider height="20px" />
                <ButtonV2
                  className={classes.acceptedButton}
                  onClick={redirectToBuyerRegistration}
                  styleType="link"
                  data-testid={'fill-out-the-registration-form'}
                >
                  Fill out the registration form
                </ButtonV2>
                <VerticalDivider height="20px" />
                <LabraToolTipV2
                  description={`${copiedBuyerForm ? 'Copied' : ''}`}
                  tooltipPlacement="top"
                  icon={
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ width: '12px', height: '12px' }}
                    />
                  }
                  tooltipPlacmentGap="0"
                >
                  <ButtonV2
                    className={classes.acceptedButton}
                    styleType="link"
                    data-testid={'copy-form-link'}
                    onClick={copyBuyerRegistrationForm}
                  >
                    Copy form link
                  </ButtonV2>
                </LabraToolTipV2>
              </div>
            </div>
          )}
        </div>
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS && (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
          status="In Progress"
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          divider
          content={
            <div data-testid={'inprogress-offer-content'}>
              The offer is being created in the AWS Marketplace and will be
              ready in approximately 5 minutes. Please refresh the page
              periodically to check the status.
            </div>
          }
        />
      )}
      {offerExpiringSoon && (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.YELLOW}
          status=""
          backgroundColor={StatusBarColorsV4.YELLOW}
          divider
          content={
            <div
              className={classes.expired}
              data-testid={'expiring-soon-offer-content'}
            >
              <div className={classes.offerExpiringSoon}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    color: '#2E3132',
                  }}
                />
                <div>
                  {dayDiff === 1
                    ? 'Offer expires today.'
                    : `Offer expiring in ${dayDiff} days.
                  Expires at ${fullDateExpirationTimeForHeader}.`}
                </div>
              </div>
              {planType !== PlanType.AMI && (
                <>
                  <VerticalDivider height="24px" />
                  {
                    <div>
                      <ExtendPrivateOfferExpiration
                        {...{
                          ...extendExpirationProps,
                          offerExpirationDate: formatedOfferExpirationDate!,
                        }}
                      />
                    </div>
                  }
                </>
              )}
            </div>
          }
        />
      )}
      <OfferHeaderActions
        privateOffer={privateOffer}
        crmOpportunityId={crmOpportunityId}
        cloudMarketplace={'AWS'}
        externalBaseURL={externalBaseURL}
        search={search}
        planType={planType}
        crmType={crmType}
        partnerId={partnerId}
        viewTracking={viewTracking}
      />
    </>
  )
}
